import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';

import { IFacetOption } from 'shared/types/ConstructorFacet';
import { SearchFilterLink } from 'search/components/searchGrid/SearchFilterLink';
import { SearchFiltersHoverAndSelectedStyle } from 'search/styles/SearchStyles';
import { UnorderedList } from 'shared/components/UnorderedList';
import { getAppliedFilters } from 'shared/utils/gridEventTrackingUtils';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { getColorStrings } from 'search/enums/translations';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

interface ISearchFilterColorDesktop {
  facetType: string;
  filterOptions: Array<IFacetOption>;
  resultIds: Array<string>;
}

export const SearchFilterColorDesktop = ({
  facetType,
  filterOptions: colorOptions,
  resultIds,
}: ISearchFilterColorDesktop) => {
  const eventTrackingEventContext = useEventContext();
  const { query } = useRouter();
  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const handleOnClick = (optionValue) => {
    sendTrackingEvent('FILTER_APPLY_TAP', {
      event_context: eventTrackingEventContext,
      filter_name: facetType,
      filter_selection: optionValue,
      result_id: resultIds[0],
      service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(query?.query && { search_string: query.query.toString() }),
    });
  };

  return (
    <SearchFilterColorDesktop.Wrapper data-qa="color_filter_list">
      {colorOptions.map((option) => {
        return (
          <SearchFilterColorDesktop.Row key={option.value} data-qa="color_filter">
            <SearchFilterLink facetType={facetType} option={option}>
              <SearchFilterColorDesktop.ColorSwatch
                $color={option.data?.hex}
                $swatchUrl={option.data?.web?.imageUrl}
                data-qa="color_filter_swatch"
                onClick={() => handleOnClick(option.value)}
              />
            </SearchFilterLink>
            <SearchFilterColorDesktop.Name
              $isSelected={option.status === 'selected'}
              data-qa="color_filter_name"
            >
              {getColorStrings(option.display_name)}
            </SearchFilterColorDesktop.Name>
          </SearchFilterColorDesktop.Row>
        );
      })}
    </SearchFilterColorDesktop.Wrapper>
  );
};

SearchFilterColorDesktop.Wrapper = styled(UnorderedList)`
  margin-top: 12px !important;
`;

SearchFilterColorDesktop.ColorSwatch = styled.a<{ $color: string; $swatchUrl?: string }>`
  order: 2;
  width: 20px;
  height: 20px;
  background: ${({ $color }) => $color || ''};
  background-image: ${({ $swatchUrl }) => $swatchUrl && `url(${$swatchUrl})`};
  background-size: contain;
  cursor: pointer;
`;

SearchFilterColorDesktop.Name = styled.div<{ $isSelected: boolean }>`
  order: 1;
  cursor: default;
  opacity: 0;
  visibility: hidden;
  margin-right: 8px;
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      ${SearchFiltersHoverAndSelectedStyle};
      opacity: 1;
      visibility: visible;
    `};
`;

SearchFilterColorDesktop.Row = styled.li`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  ${SearchFilterColorDesktop.ColorSwatch}:hover ~ ${SearchFilterColorDesktop.Name} {
    opacity: 1;
    visibility: visible;
  }
`;
